import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * LinksSheet – bottom sheet portaled to document.body.
 *
 * Using createPortal fixes scroll-conflict bugs that happen when the sheet is
 * rendered inside a nested scrollable div or a positioned ancestor.
 * Body scroll is locked while the sheet is open.
 */
const LinksSheet = ({ isOpen, restaurant, theme, onClose }) => {
  // ── Lock body scroll while open ──────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  const themeColor    = theme?.secondary || '#222';
  const themeText     = theme?.primary   || '#fff';

  const links = [
    restaurant?.phone && {
      label: 'Call us',
      sub:   restaurant.phone,
      href:  `tel:${restaurant.phone}`,
      icon:  <PhoneIcon />,
    },
    restaurant?.website && {
      label: 'Website',
      sub:   restaurant.website,
      href:  restaurant.website,
      icon:  <WebIcon />,
    },
    restaurant?.gmapLink && {
      label: 'Google Maps',
      sub:   restaurant.address || 'Open in Maps',
      href:  restaurant.gmapLink,
      icon:  <MapIcon />,
    },
    restaurant?.socialLinks?.instagram && {
      label: 'Instagram',
      sub:   restaurant.socialLinks.instagram,
      href:  restaurant.socialLinks.instagram,
      icon:  <InstagramIcon />,
    },
    restaurant?.socialLinks?.facebook && {
      label: 'Facebook',
      sub:   restaurant.socialLinks.facebook,
      href:  restaurant.socialLinks.facebook,
      icon:  <FacebookIcon />,
    },
  ].filter(Boolean);

  const sheet = (
    <AnimatePresence>
      {isOpen && (
        // ── Backdrop ──────────────────────────────────────────────────
        <motion.div
          key="links-backdrop"
          className="fixed inset-0 z-[9999] flex items-end justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          {/* ── Sheet panel ─────────────────────────────────────────── */}
          <motion.div
            key="links-panel"
            className="bg-white rounded-t-2xl w-full max-w-md shadow-2xl"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 400) onClose();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-8 pt-2">
              <h2 className="text-base font-bold text-gray-900 mb-4">
                {restaurant?.name}
              </h2>

              <div className="space-y-2">
                {links.map(({ label, sub, href, icon }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith('tel') ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 active:bg-gray-100 transition-colors"
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: themeColor, color: themeText }}
                    >
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{label}</p>
                      <p className="text-xs text-gray-400 truncate">{sub}</p>
                    </div>
                    <ChevronIcon />
                  </a>
                ))}

                {links.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    No links available.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal to document.body so it's never clipped by a parent stacking/scroll context
  return ReactDOM.createPortal(sheet, document.body);
};

export default LinksSheet;

// ── tiny inline SVG icons ─────────────────────────────────────────────
const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
  </svg>
);
const WebIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
  </svg>
);
const MapIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
const ChevronIcon = () => (
  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
